import React, { useState, useEffect } from "react";
import { FaStar, FaMoon, FaMosque } from "react-icons/fa";
import { GiLantern, GiMoon, GiCrescentBlade } from "react-icons/gi";
import styles from "./ProphetBirthdayBanner.module.css";

const ProphetBirthdayBanner = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [particles, setParticles] = useState([]);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [autoCloseCountdown, setAutoCloseCountdown] = useState(7);

  // تاريخ المولد النبوي الشريف
  const eidMiladDate = new Date("2025-09-04T00:00:00");

  // التحقق من عرض البانر
  useEffect(() => {
    const checkBannerVisibility = () => {
      try {
        // التحقق من أن المناسبة لم تنتهي بعد
        const now = new Date();
        if (now >= eidMiladDate) {
          setIsVisible(false);
          return;
        }

        // إذا وصلنا إلى هنا، فهذا يعني أن البانر يجب أن يظهر
        // (تم التحقق من sessionStorage في Loading component)
        setIsVisible(true);
        // تخزين أن البانر تم عرضه
        sessionStorage.setItem("prophetBirthdayBannerShown", "true");
      } catch (error) {
        console.error("خطأ في التحقق من عرض البانر:", error);
        setIsVisible(false);
      }
    };

    checkBannerVisibility();
  }, []);

  // إنشاء جزيئات متحركة
  useEffect(() => {
    if (!isVisible) return;

    try {
      const newParticles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 2,
        speed: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.3,
      }));
      setParticles(newParticles);
    } catch (error) {
      console.error("خطأ في إنشاء الجزيئات:", error);
    }
  }, [isVisible]);

  // حساب الوقت المتبقي حتى المولد النبوي الشريف
  useEffect(() => {
    if (!isVisible) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = eidMiladDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        // إذا وصلنا للتاريخ، نغلق البانر
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        handleClose();
      }
    };

    // حساب الوقت المتبقي كل ثانية
    const timer = setInterval(calculateTimeLeft, 1000);

    // حساب الوقت المتبقي فوراً
    calculateTimeLeft();

    return () => clearInterval(timer);
  }, [isVisible]);

  // إخفاء البانر بعد 7 ثواني مع عداد تنازلي
  useEffect(() => {
    if (!isVisible) return;

    const autoCloseTimer = setInterval(() => {
      setAutoCloseCountdown((prev) => {
        if (prev <= 1) {
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(autoCloseTimer);
    };
  }, [isVisible]);

  const handleClose = () => {
    console.log("handleClose called, onClose exists:", !!onClose);
    setIsVisible(false);
    if (onClose) {
      console.log("Calling onClose function");
      onClose();
    } else {
      console.log("onClose function not provided");
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.bannerContainer}>
      {/* خلفية متدرجة */}
      <div className={styles.backgroundGradient}></div>

      {/* جزيئات متحركة */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={styles.particle}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity,
            animationDuration: `${particle.speed}s`,
          }}
        />
      ))}

      {/* فوانيس طائرة من اليمين */}
      <div className={styles.flyingLanternsRight}>
        <GiLantern
          className={styles.flyingLantern}
          style={{ animationDelay: "0s" }}
        />
        <GiLantern
          className={styles.flyingLantern}
          style={{ animationDelay: "1s" }}
        />
        <GiLantern
          className={styles.flyingLantern}
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* فوانيس طائرة من الشمال */}
      <div className={styles.flyingLanternsLeft}>
        <GiLantern
          className={styles.flyingLantern}
          style={{ animationDelay: "0.5s" }}
        />
        <GiLantern
          className={styles.flyingLantern}
          style={{ animationDelay: "1.5s" }}
        />
        <GiLantern
          className={styles.flyingLantern}
          style={{ animationDelay: "2.5s" }}
        />
      </div>

      {/* المحتوى الرئيسي */}
      <div className={styles.content}>
        <div className={styles.iconContainer}>
          <GiLantern className={styles.lampIcon} />
          <FaMosque className={styles.mosqueIcon} />
          <GiMoon className={styles.crescentIcon} />
        </div>

        <div className={styles.textContent}>
          <h1 className={styles.title}>
            <span className={styles.highlight}>المولد النبوي الشريف</span>
          </h1>
          <p className={styles.subtitle}>مولد خير البشر ﷺ</p>
          <p className={styles.date}>٤ ربيع الأول ١٤٤٧ هـ</p>
          <p className={styles.gregorianDate}>٤ سبتمبر ٢٠٢٥ م - يوم الخميس</p>
          <div className={styles.infoContainer}>
            <p className={styles.infoText}>
              "وَمَا أَرْسَلْنَاكَ إِلَّا رَحْمَةً لِّلْعَالَمِينَ"
            </p>
            <p className={styles.infoSubtext}>سورة الأنبياء - الآية ١٠٧</p>
          </div>
          <div className={styles.countdownContainer}>
            <p className={styles.countdownText}>
              باقي على المولد النبوي الشريف
            </p>
            <div className={styles.countdownTimer}>
              <span className={styles.countdownItem}>
                <span className={styles.countdownNumber}>{timeLeft.days}</span>
                <span className={styles.countdownLabel}>أيام</span>
              </span>
              <span className={styles.countdownSeparator}>:</span>
              <span className={styles.countdownItem}>
                <span className={styles.countdownNumber}>
                  {timeLeft.hours.toString().padStart(2, "0")}
                </span>
                <span className={styles.countdownLabel}>ساعة</span>
              </span>
              <span className={styles.countdownSeparator}>:</span>
              <span className={styles.countdownItem}>
                <span className={styles.countdownNumber}>
                  {timeLeft.minutes.toString().padStart(2, "0")}
                </span>
                <span className={styles.countdownLabel}>دقيقة</span>
              </span>
              <span className={styles.countdownSeparator}>:</span>
              <span className={styles.countdownItem}>
                <span className={styles.countdownNumber}>
                  {timeLeft.seconds.toString().padStart(2, "0")}
                </span>
                <span className={styles.countdownLabel}>ثانية</span>
              </span>
            </div>
          </div>
          <p className={styles.autoCloseMessage}>
            ⏰ سيتم إغلاق هذا البانر تلقائياً خلال {autoCloseCountdown} ثانية
          </p>
        </div>

        <div className={styles.starsContainer}>
          {[...Array(5)].map((_, i) => (
            <FaStar
              key={i}
              className={styles.star}
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>

      {/* زر الإغلاق */}
      <button
        className={styles.closeButton}
        onClick={handleClose}
        aria-label="إغلاق البانر"
      >
        ×
      </button>
    </div>
  );
};

export default ProphetBirthdayBanner;
